import React from 'react';
import { ComponentConfig } from '@measured/puck';
import { GoogleMapPin } from './googleMapPin';
import { marginFields, MarginProps } from '../../puck/reusable-props/margin';
import { paddingFields, PaddingProps } from '../../puck/reusable-props/padding';
import { backgroungFields } from '../../puck/reusable-props/backgroungFields';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
} from '../../utils/helper';
import CustomTooltip from '../../../components/common/tooltip';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../services/constants';

function formatShopifyAddress(addr?: {
  Address1?: string;
  Address2?: string;
  City?: string;
  ProvinceCode?: string;
  zip?: string;
  CountryCode?: string;
}): string {
  if (!addr) return '';
  return [
    addr.Address1,
    addr.Address2,
    addr.City,
    addr.ProvinceCode,
    addr.zip,
    addr.CountryCode,
  ]
    .filter(Boolean)
    .join(', ');
}

export type GoogleMapProps = {
  zoom: number;
  border: IBorderValueProps;
  background: {
    color: string;
    isInherit: boolean;
  };
} & MarginProps &
  PaddingProps &
  showComponentProps;

export const GoogleMap = ({
  useAppSelector,
}: ReduxDispatchProps &
  ReduxSelectorProps): ComponentConfig<GoogleMapProps> => {
  return {
    label: (
      <CustomTooltip title="Google Map Pin">
        <span>Google Map Pin</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      zoom: {
        type: 'number',
        label: 'Zoom Level',
      },
      ...marginFields,
      ...paddingFields,
      ...backgroungFields,
      border: {
        type: 'custom',
        label: 'Border',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
    },

    defaultProps: {
      zoom: 14,
      ...borderProps,
      background: {
        color: '#ffffff',
        isInherit: false,
      },
      margin: {
        desktop: { top: '0', bottom: '0', left: '0', right: '0' },
        tablet: { top: '0', bottom: '0', left: '0', right: '0' },
        mobile: { top: '0', bottom: '0', left: '0', right: '0' },
      },
      padding: {
        deviceType: 'desktop',
        paddingTop: { desktop: '0', tablet: '0', mobile: '0' },
        paddingBottom: { desktop: '0', tablet: '0', mobile: '0' },
        paddingLeft: { desktop: '0', tablet: '0', mobile: '0' },
        paddingRight: { desktop: '0', tablet: '0', mobile: '0' },
      },
      showComponent: ['desktop', 'tablet', 'mobile'],
    },

    render: ({
      zoom,
      margin,
      padding,
      border,
      background,
      showComponent,
      editMode,
    }) => {
      const shippingAddress = useAppSelector(
        state => state.cart.cartData?.ShippingAddress,
      );
      const googleApiKey = useAppSelector(
        state => state.store.storeData?.GoogleApiKey,
      );
      const formattedAddress = formatShopifyAddress(shippingAddress);

      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);

      if (!showComponent?.includes(currentDeviceType)) return <></>;

      const marginStyles = getMarginStyles(margin, currentDeviceType);
      const paddingStyles = getPaddingStyles(padding, currentDeviceType);
      const borderStyles = getBorderProps(border, currentDeviceType);

      return (
        <div
          className="w-full"
          style={{
            ...marginStyles,
            ...paddingStyles,
            ...borderStyles,
            backgroundColor: background?.isInherit
              ? 'inherit'
              : background?.color,
          }}>
          <GoogleMapPin
            address={formattedAddress}
            apiKey={googleApiKey}
            zoom={zoom}
          />
        </div>
      );
    },
  };
};
